import React, { useEffect, useState } from "react";
import { Box, Button, IconButton, Paper, Slide, Stack, Typography, useMediaQuery } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import IosShareIcon from "@mui/icons-material/IosShare";
import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined";
import { useLocation } from "react-router-dom";
import Logo from "./Logo";

const DISMISS_KEY = "caro.installPrompt.dismissedAt";
const DISMISS_DAYS = 14;
const SHOW_DELAY_MS = 2500;
const BOTTOM_NAV_PATHS = ["/home", "/chats", "/profile", "/myCars", "/support", "/membership"];

const PHONE_QUERY = "(max-width: 899px) and (pointer: coarse)";

const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;

const isIos = () =>
  /iphone|ipad|ipod/i.test(window.navigator.userAgent) ||
  // iPadOS se presenta como Mac con pantalla táctil
  (window.navigator.platform === "MacIntel" && window.navigator.maxTouchPoints > 1);

const wasDismissedRecently = () => {
  try {
    const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || 0);
    return Date.now() - dismissedAt < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
};

// Sólo se reemplaza el aviso del navegador cuando se va a mostrar el propio (celular, sin descartar).
// En computadora se deja el de Chrome intacto y no se frena el evento.
const shouldTakeOverInstall = () => window.matchMedia(PHONE_QUERY).matches && !wasDismissedRecently();

// El navegador puede avisar que la app es instalable antes de que se monte el componente
let earlyInstallEvent = null;
window.addEventListener("beforeinstallprompt", (event) => {
  if (!shouldTakeOverInstall()) return;
  event.preventDefault();
  earlyInstallEvent = event;
});

const rememberDismissal = () => {
  try {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  } catch {
    // Sin almacenamiento se vuelve a ofrecer en la próxima visita
  }
};

// Ofrece instalar la app en celulares cuando todavía no está instalada
const InstallPrompt = () => {
  const location = useLocation();
  const isPhone = useMediaQuery(PHONE_QUERY);
  const [installEvent, setInstallEvent] = useState(() => earlyInstallEvent);
  const [ios] = useState(isIos);
  const [installed, setInstalled] = useState(isStandalone);
  const [visible, setVisible] = useState(false);

  // Android/Chrome: el navegador avisa cuando la app se puede instalar
  useEffect(() => {
    const onBeforeInstall = (event) => {
      if (!shouldTakeOverInstall()) return;
      event.preventDefault();
      setInstallEvent(event);
    };
    const onInstalled = () => {
      setInstalled(true);
      setVisible(false);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const canOffer = isPhone && !installed && (installEvent || ios) && !wasDismissedRecently();

  useEffect(() => {
    if (!canOffer) {
      setVisible(false);
      return undefined;
    }
    const timer = setTimeout(() => setVisible(true), SHOW_DELAY_MS);
    return () => clearTimeout(timer);
  }, [canOffer]);

  const dismiss = () => {
    rememberDismissal();
    setVisible(false);
  };

  const install = async () => {
    if (!installEvent) return;
    earlyInstallEvent = null;
    installEvent.prompt();
    const { outcome } = await installEvent.userChoice;
    setInstallEvent(null);
    if (outcome === "accepted") setInstalled(true);
    else rememberDismissal();
    setVisible(false);
  };

  const aboveBottomNav = BOTTOM_NAV_PATHS.includes(location.pathname) || location.pathname.startsWith("/ver/");

  return (
    <Slide direction="up" in={visible} mountOnEnter unmountOnExit>
      <Paper
        role="dialog"
        aria-label="Instalar Caro"
        elevation={8}
        sx={{
          position: "fixed",
          left: 12,
          right: 12,
          bottom: aboveBottomNav ? 72 : 12,
          zIndex: (theme) => theme.zIndex.snackbar,
          p: 2,
          borderRadius: 4,
          border: 1,
          borderColor: "divider",
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Logo size={44} />
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 800 }}>Instalá Caro</Typography>
            <Typography variant="body2" color="text.secondary">
              {installEvent
                ? "Agregala a tu pantalla de inicio y abrila como una app, con notificaciones de tus chats."
                : "Agregala a tu pantalla de inicio para abrirla como una app."}
            </Typography>
          </Box>
          <IconButton size="small" aria-label="Cerrar" onClick={dismiss} sx={{ mt: -0.5, mr: -0.5 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>

        {installEvent ? (
          <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1.5 }}>
            <Button color="inherit" onClick={dismiss}>
              Ahora no
            </Button>
            <Button variant="contained" color="secondary" onClick={install}>
              Instalar
            </Button>
          </Stack>
        ) : (
          <Stack spacing={1} sx={{ mt: 1.5, p: 1.5, borderRadius: 2.5, bgcolor: "background.default" }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" sx={{ fontWeight: 700 }}>1.</Typography>
              <Typography variant="body2">Tocá</Typography>
              <IosShareIcon fontSize="small" sx={{ color: "#007AFF" }} />
              <Typography variant="body2">
                <strong>Compartir</strong> en Safari
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" sx={{ fontWeight: 700 }}>2.</Typography>
              <Typography variant="body2">Elegí</Typography>
              <AddBoxOutlinedIcon fontSize="small" />
              <Typography variant="body2">
                <strong>Agregar a inicio</strong>
              </Typography>
            </Stack>
          </Stack>
        )}
      </Paper>
    </Slide>
  );
};

export default InstallPrompt;
